/**
 * TouchField — the shared multitouch input layer.
 *
 * Built on Pointer Events so touch, pen and mouse all funnel through one path.
 * Positions are normalized to UV space: x,y in [0,1] with the ORIGIN AT
 * BOTTOM-LEFT (y up) to match GLSL texture/`gl_FragCoord` conventions, so a
 * touch can be handed straight to a shader.
 *
 * Per-frame velocity is computed in `update(dt)` (units: uv/second), so
 * gesture-velocity sketches (Synesthetic Frequency Field) read a stable value
 * rather than raw pointermove deltas.
 */

export interface Touch {
  /** Pointer id from the browser. */
  id: number;
  /** Normalized position, [0,1], y up. */
  x: number;
  y: number;
  /** Position at the previous `update()`, for velocity. */
  px: number;
  py: number;
  /** Velocity in uv/second, set by `update()`. */
  vx: number;
  vy: number;
  /** 0..1 pressure (falls back to 0.5 for devices without it). */
  pressure: number;
  /** Performance-clock time the touch began (ms). */
  startTime: number;
  /** Where the touch began, for tap-vs-drag classification. */
  startX: number;
  startY: number;
}

export class TouchField {
  private readonly el: HTMLElement;
  private readonly map = new Map<number, Touch>();
  /** The most recently moved/added touch id, for gaze-tracking sketches. */
  private recentId: number | null = null;
  /** Positions (uv) of presses since the last consumePresses(), for one-shot stamps. */
  private presses: Array<{ x: number; y: number }> = [];
  /** performance.now() of the last pointer activity, for idle detection. */
  lastActivity = performance.now();

  private readonly onDown: (e: PointerEvent) => void;
  private readonly onMove: (e: PointerEvent) => void;
  private readonly onUp: (e: PointerEvent) => void;

  constructor(el: HTMLElement) {
    this.el = el;
    this.onDown = (e) => this.handleDown(e);
    this.onMove = (e) => this.handleMove(e);
    this.onUp = (e) => this.handleUp(e);
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onUp);
    el.addEventListener('pointerleave', this.onUp);
  }

  private toUv(e: PointerEvent): { x: number; y: number } {
    const r = this.el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = 1 - (e.clientY - r.top) / r.height; // flip: y up
    return { x, y };
  }

  private handleDown(e: PointerEvent): void {
    (this.el as HTMLElement).setPointerCapture?.(e.pointerId);
    const { x, y } = this.toUv(e);
    this.map.set(e.pointerId, {
      id: e.pointerId,
      x, y, px: x, py: y, vx: 0, vy: 0,
      pressure: e.pressure || 0.5,
      startTime: performance.now(),
      startX: x, startY: y,
    });
    this.recentId = e.pointerId;
    this.presses.push({ x, y });
    this.lastActivity = performance.now();
  }

  private handleMove(e: PointerEvent): void {
    const t = this.map.get(e.pointerId);
    if (!t) return;
    const { x, y } = this.toUv(e);
    t.x = x;
    t.y = y;
    t.pressure = e.pressure || t.pressure;
    this.recentId = e.pointerId;
    this.lastActivity = performance.now();
  }

  private handleUp(e: PointerEvent): void {
    if (this.map.delete(e.pointerId) && this.recentId === e.pointerId) {
      // Keep recentId pointing at the freshest remaining touch, if any.
      let latest: Touch | null = null;
      for (const t of this.map.values()) {
        if (!latest || t.startTime > latest.startTime) latest = t;
      }
      this.recentId = latest ? latest.id : this.recentId;
    }
    this.lastActivity = performance.now();
  }

  /** Recompute per-touch velocity. Call once per frame, before sketches read. */
  update(dt: number): void {
    if (dt <= 0) dt = 1 / 60;
    for (const t of this.map.values()) {
      t.vx = (t.x - t.px) / dt;
      t.vy = (t.y - t.py) / dt;
      t.px = t.x;
      t.py = t.y;
    }
  }

  get count(): number {
    return this.map.size;
  }

  get active(): boolean {
    return this.map.size > 0;
  }

  /** All currently-pressed touches. */
  list(): Touch[] {
    return [...this.map.values()];
  }

  /**
   * Return (and clear) the positions of touches pressed since the last call.
   * Lets a sketch perform one-shot actions on touch-down (e.g. stamp a seed)
   * rather than acting every frame a finger is held.
   */
  consumePresses(): Array<{ x: number; y: number }> {
    const p = this.presses;
    this.presses = [];
    return p;
  }

  /** Freshest touch (the one a "curious" sketch should track). */
  get mostRecent(): Touch | null {
    if (this.recentId == null) return null;
    return this.map.get(this.recentId) ?? null;
  }

  /** Seconds since any pointer activity — drives attract/idle mode. */
  idleSeconds(): number {
    return (performance.now() - this.lastActivity) / 1000;
  }

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
    this.el.removeEventListener('pointerleave', this.onUp);
    this.map.clear();
  }
}
